import { useState, useEffect } from 'react';

export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const BREAKPOINTS_REM: Array<[Breakpoint, number]> = [
  ['sm', 40],
  ['md', 48],
  ['lg', 64],
  ['xl', 80],
  ['2xl', 96],
];

function resolveBreakpoint(): Breakpoint {
  if (typeof window === 'undefined') return 'xs';

  const active = BREAKPOINTS_REM.filter(([, minWidth]) => window.matchMedia(`(min-width: ${minWidth}rem)`).matches);

  return active.length > 0 ? active[active.length - 1][0] : 'xs';
}

function subscribe(query: MediaQueryList, listener: () => void): void {
  if (typeof query.addEventListener === 'function') query.addEventListener('change', listener);
  else query.addListener(listener);
}

function unsubscribe(query: MediaQueryList, listener: () => void): void {
  if (typeof query.removeEventListener === 'function') query.removeEventListener('change', listener);
  else query.removeListener(listener);
}

export function useBreakpoint(): { breakpoint: Breakpoint } {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(resolveBreakpoint);

  useEffect(() => {
    const queries = BREAKPOINTS_REM.map(([, minWidth]) => window.matchMedia(`(min-width: ${minWidth}rem)`));

    const update = () => setBreakpoint(resolveBreakpoint());
    update();

    queries.forEach((query) => subscribe(query, update));

    return () => queries.forEach((query) => unsubscribe(query, update));
  }, []);

  return { breakpoint };
}
